import { useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import type {
  Section12Gender,
  Section12Language,
  Section12Specialism,
} from "@thalamos/aspire-common";
import { SECTION_12_GENDER_LABELS } from "@thalamos/aspire-common";

import { Typeahead } from "~/components/design-system/Typeahead/Typeahead.js";

interface FilterPanelProps {
  languages: Section12Language[];
  specialisms: Section12Specialism[];
  selectedSpecialismIds: string[];
  selectedGenders: Section12Gender[];
  selectedLanguageIds: string[];
  onSpecialismsChange: (specialismIds: string[]) => void;
  onGendersChange: (genders: Section12Gender[]) => void;
  onLanguageIdsChange: (languageIds: string[]) => void;
  onClearFilters: () => void;
}

const GENDER_OPTIONS = Object.keys(
  SECTION_12_GENDER_LABELS,
) as Section12Gender[];

export function FilterPanel({
  languages,
  specialisms,
  selectedSpecialismIds,
  selectedGenders,
  selectedLanguageIds,
  onSpecialismsChange,
  onGendersChange,
  onLanguageIdsChange,
  onClearFilters,
}: FilterPanelProps) {
  const [specialismInputValue, setSpecialismInputValue] = useState("");

  const selectedSpecialisms = specialisms.filter((s) =>
    selectedSpecialismIds.includes(s.id),
  );

  const handleSpecialismAdd = (specialismName: string) => {
    const specialism = specialisms.find(
      (s) => s.specialismName === specialismName,
    );
    if (specialism && !selectedSpecialismIds.includes(specialism.id)) {
      onSpecialismsChange([...selectedSpecialismIds, specialism.id]);
      setSpecialismInputValue("");
    }
  };

  const handleSpecialismRemove = (specialismId: string) => {
    onSpecialismsChange(
      selectedSpecialismIds.filter((id) => id !== specialismId),
    );
  };

  const hasActiveFilters =
    selectedSpecialismIds.length > 0 ||
    selectedGenders.length > 0 ||
    selectedLanguageIds.length > 0;

  const selectedLanguages = languages.filter((language) =>
    selectedLanguageIds.includes(language.id),
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Filters
        </Typography>
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={onClearFilters}
            data-testid="clear-filters-button"
            sx={{ fontSize: "0.7rem", p: 0.5 }}
          >
            Clear
          </Button>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 1.5, bgcolor: "grey.50", border: 1, borderColor: "divider" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Specialisms Filter */}
          <Box>
            <FormLabel
              component="legend"
              sx={{ mb: 0.5, fontSize: "0.8rem", fontWeight: 500 }}
            >
              Specialisms
            </FormLabel>
            <Typeahead
              placeholder="Add specialism..."
              enableFuse={true}
              options={specialisms.filter(
                (s) => !selectedSpecialismIds.includes(s.id),
              )}
              inputValue={specialismInputValue}
              optionsKey="specialismName"
              name="specialisms"
              getOptionLabel={(spec: Section12Specialism | string) =>
                typeof spec === "string" ? spec : spec.specialismName
              }
              onInputChange={(value: string | null) => {
                if (value) {
                  const specialism = specialisms.find(
                    (s) => s.specialismName === value,
                  );
                  if (
                    specialism &&
                    !selectedSpecialismIds.includes(specialism.id)
                  ) {
                    handleSpecialismAdd(value);
                  } else {
                    setSpecialismInputValue(value);
                  }
                } else {
                  setSpecialismInputValue("");
                }
              }}
              disablePortal
              data-testid="specialism-typeahead"
            />
            {selectedSpecialisms.length > 0 && (
              <Box
                sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.5 }}
              >
                {selectedSpecialisms.map((spec) => (
                  <Chip
                    key={spec.id}
                    label={spec.specialismName}
                    onDelete={() => {
                      handleSpecialismRemove(spec.id);
                    }}
                    size="small"
                    color="primary"
                    sx={{ height: 24, fontSize: "0.75rem" }}
                    data-testid={`specialism-chip-${spec.id}`}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Gender Filter */}
          <Box>
            <FormLabel
              component="legend"
              sx={{ mb: 0.5, fontSize: "0.8rem", fontWeight: 500 }}
            >
              Gender
            </FormLabel>
            <Autocomplete
              multiple
              options={GENDER_OPTIONS}
              value={selectedGenders}
              onChange={(_event, newValue) => {
                onGendersChange(newValue);
              }}
              getOptionLabel={(option) => SECTION_12_GENDER_LABELS[option]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedGenders.length === 0 ? "Any gender" : ""}
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={SECTION_12_GENDER_LABELS[option]}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                ))
              }
              size="small"
              disablePortal
              sx={{ bgcolor: "white" }}
              data-testid="gender-select"
            />
          </Box>

          {/* Language Filter */}
          <Box>
            <FormLabel
              component="legend"
              sx={{ mb: 0.5, fontSize: "0.8rem", fontWeight: 500 }}
            >
              Languages
            </FormLabel>
            <Autocomplete
              multiple
              options={languages}
              value={selectedLanguages}
              onChange={(_event, newValue) => {
                onLanguageIdsChange(newValue.map((language) => language.id));
              }}
              getOptionLabel={(option) => option.languageName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedLanguageIds.length === 0 ? "Any language" : ""
                  }
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.languageName}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                ))
              }
              size="small"
              disablePortal
              sx={{ bgcolor: "white" }}
              data-testid="language-filter-autocomplete"
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
